
import { NavLink, useLocation } from "react-router-dom"
import Container from "../../Atoms/layouts/container/Container"
import Logo from "../../Atoms/logo/logo"
import OurApps from "../our_apps/OurApps"
import style from "./TopNavBar.module.scss"
import { useCallback, useEffect, useState } from "react"

const NAV_ITEMS = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Research", to: "/lushai-analytics" },
  { label: "About Us", to: "/about-us" },
  { label: "Blogs", to: "/blogs" },
  { label: "Contact", to: "/contact" },
  { label: "Careers", to: "/careers", overflow: true },
  { label: "AI Hub", to: "/ai-hub", overflow: true },
  { label: "Learning", to: "/education", overflow: true },
];

const OVERFLOW_ITEMS = NAV_ITEMS.filter((item) => item.overflow);

const isNavItemActive = (pathname, to) => {
  if (to === "/") return pathname === "/";
  return pathname === to || pathname.startsWith(`${to}/`);
};

export default function TopNavBar() {

  const location = useLocation();
  const [mobileMenuActive, setMobileMenuActive] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [navListEl, setNavListEl] = useState(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0, opacity: 0 });

  const navListRef = useCallback((node) => {
    setNavListEl(node);
  }, []);

  const overflowActive = OVERFLOW_ITEMS.some((item) =>
    isNavItemActive(location.pathname, item.to)
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!moreMenuOpen) return;

    const closeMore = (event) => {
      if (!event.target.closest(`.${style.MoreWrap}`)) {
        setMoreMenuOpen(false);
      }
    };

    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setMoreMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", closeMore);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeMore);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [moreMenuOpen]);

  useEffect(() => {
    if (!mobileMenuActive) return;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setMobileMenuActive(false);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [mobileMenuActive]);

  const updateIndicator = useCallback(() => {
    if (!navListEl || window.innerWidth <= 1024) {
      setIndicator((prev) => ({ ...prev, opacity: 0 }));
      return;
    }

    const overflowHidden = window.innerWidth <= 1120;
    let target = navListEl.querySelector(`.${style.NavLink}.${style.active}`);

    if (!target && overflowHidden && overflowActive) {
      target = navListEl.querySelector(`.${style.MoreTrigger}`);
    }

    if (!target) {
      setIndicator((prev) => ({ ...prev, opacity: 0 }));
      return;
    }

    const listRect = navListEl.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();

    setIndicator({
      left: targetRect.left - listRect.left,
      width: targetRect.width,
      opacity: 1,
    });
  }, [navListEl, location.pathname, overflowActive]);

  useEffect(() => {
    const frame = requestAnimationFrame(updateIndicator);
    return () => cancelAnimationFrame(frame);
  }, [updateIndicator, mobileMenuActive, moreMenuOpen]);

  useEffect(() => {
    window.addEventListener("resize", updateIndicator);
    return () => window.removeEventListener("resize", updateIndicator);
  }, [updateIndicator]);

  const navLinkClass = ({ isActive }) =>
    isActive ? `${style.NavLink} ${style.active}` : style.NavLink;

  return (
    <header className={`${style.NavBar} ${scrolled ? style.scrolled : ""}`} role="banner">
      <Container className={style.NavWrap}>
        <div className={style.NavShell}>
          <div className={style.LogoContainer}>
            <NavLink to="/" aria-label="LushAITech home" className={style.LogoLink}>
              <Logo />
            </NavLink>
          </div>

          <nav
            id="main-navigation"
            className={`${style.Navigations} ${mobileMenuActive ? style.active : ""}`}
            aria-label="Main navigation"
          >
            <button
              className={style.MobileMenuClose}
              onClick={() => setMobileMenuActive(false)}
              aria-label="Close menu"
              type="button"
            >
              <ion-icon name="close-outline"></ion-icon>
            </button>

            <ul ref={navListRef} className={style.NavList}>
              <span
                className={style.NavIndicator}
                style={{
                  left: indicator.left,
                  width: indicator.width,
                  opacity: indicator.opacity,
                }}
                aria-hidden="true"
              />
              {NAV_ITEMS.map((item) => (
                <li
                  key={item.to}
                  className={item.overflow ? style.NavItemOverflow : undefined}
                >
                  <NavLink
                    to={item.to}
                    onClick={() => setMobileMenuActive(false)}
                    className={navLinkClass}
                    end={item.to === "/"}
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
              <li className={style.MoreWrap}>
                <button
                  type="button"
                  className={`${style.MoreTrigger} ${moreMenuOpen ? style.open : ""} ${overflowActive ? style.active : ""}`}
                  aria-expanded={moreMenuOpen}
                  aria-haspopup="true"
                  onClick={() => setMoreMenuOpen((open) => !open)}
                >
                  More
                  <ion-icon name="chevron-down-outline" aria-hidden="true" />
                </button>
                <div
                  className={`${style.MoreMenu} ${moreMenuOpen ? style.open : ""}`}
                  role="menu"
                >
                  {OVERFLOW_ITEMS.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      role="menuitem"
                      className={navLinkClass}
                      onClick={() => {
                        setMoreMenuOpen(false);
                        setMobileMenuActive(false);
                      }}
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              </li>
            </ul>
          </nav>

          <div className={style.NavActions}>
            <OurApps />
            <button
              className={style.MobileMenuTrigger}
              onClick={() => setMobileMenuActive(true)}
              aria-expanded={mobileMenuActive}
              aria-controls="main-navigation"
              aria-label="Open menu"
              type="button"
            >
              <ion-icon name="menu-outline"></ion-icon>
            </button>
          </div>
        </div>
      </Container>
    </header>
  )
}
